import type { WidgetDesignTabsBuilder } from '@wix/app-manifest-builder/dist/types/controller/widgetDesign/widgetDesignTabsBuilder';
import type { WriteOnlyBuilder } from '@wix/app-manifest-builder/dist/types/types';
import type { Item, PopulatedSection, PopulatedMenu } from '../types/menusTypes';
import type { WidgetDesignTabsArray } from '../types/widgets';
import type {
  AppManifestBuilder,
  TFunction,
  WidgetBuilder,
  ReportError,
  IHttpClient,
  ISiteApis,
} from '@wix/yoshi-flow-editor';
import type {
  AppManifest,
  ConnectedComponentConfig,
  ControllerStateStageData,
  GfppConfig,
  GfppDesktopConfig,
  WidgetStateStageData,
} from '@wix/platform-editor-sdk';
import {
  CART_BUTTON_CONTROLLER_WIDGET_ID,
  DISHES_CONTROLLER_WIDGET_ID,
  GHOST_STATE_CONTROLLER_WIDGET_ID,
  HEADER_CONTROLLER_WIDGET_ID,
  LIGHTBOX_CONNECTED_PARAMS,
  MENU_CONTROLLER_WIDGET_ID,
  NAVIGATION_CONTROLLER_WIDGET_ID,
  OLO_CONTROLLER_WIDGET_ID,
  SECTION_CONTROLLER_WIDGET_ID,
} from '../appConsts/consts';
import type { DispatchInfo, DispatchStateByLocation, DispatchType } from '../types/businessTypes';
import {
  CART_BUTTON_COMPONENT_IDS,
  HEADER_WIDGET_COMPONENT_IDS,
  MENU_WIDGET_COMPONENT_IDS,
  SECTION_WIDGET_COMPONENT_IDS,
} from '../appConsts/blocksIds';
import { getAllPopups } from 'root/services/popupsService';
import type { RootContext } from 'root/context/RootContext';
import { menusState } from 'root/states/MenusState';
import { dispatchState as _dispatchState } from 'root/states/DispatchState';
import type { ItemData } from 'root/types/item';
import { LiveSiteClickFulfillmentOrigin } from '@wix/restaurants-bi';
import { getAvailabilityStatusProps } from './menusUtils';
import { availabilityStatusKeys } from 'root/availabilityStatusKeys';
import type { CartLineItem, CatalogOptions } from 'root/services/cartService';
import { ADD_TO_CART_ERRORS } from 'root/services/cartService';
import { cartState } from 'root/states/cartState';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import type { PanoramaErrorMonitor } from '@wix/panorama-client/dist/types/panorama-error-monitor';
import type { ErrorMonitorUserData } from '@wix/panorama-client/dist/types/types';
import {
  getCurrentPageName,
  getPageNameByAppDefIdId,
  getPageNameByOperationId,
} from './pageOperationUtils';

type DataType = (Item & { truncated?: boolean })[] | PopulatedSection[] | PopulatedMenu[];

export const getRole = (componentId: string) => {
  return componentId.substring(1);
};

export const doesArrayDataExist = (array?: string[]) => (array?.length ? true : false);

export const hasDataChanged = ({
  prevData = [],
  nextData = [],
}: {
  prevData?: DataType;
  nextData?: DataType;
}) => {
  return (
    prevData.length !== nextData.length ||
    prevData.some(
      (item, idx) =>
        item.id !== nextData[idx].id ||
        item.revision !== nextData[idx].revision ||
        item.truncated !== nextData[idx].truncated
    )
  );
};

export const truncate = (str: string, maxChars: number) => {
  return str.length > maxChars ? `${str.slice(0, maxChars - 1)}...` : str;
};

const addTab = (
  widgetDesignTabsBuilder: WriteOnlyBuilder<WidgetDesignTabsBuilder>,
  label: string,
  roles: string[],
  dependents?: string[],
  tooltip?: string
) => {
  widgetDesignTabsBuilder.addTab((tabBuilder) => {
    tabBuilder
      .set({
        label,
        tooltip,
        dependents: dependents?.map(getRole),
      })
      .groups()
      .set({
        roles: roles.map(getRole),
      });
  });
};

export const setWidgetDesignTabs = (
  widgetDesignTabsBuilder: WriteOnlyBuilder<WidgetDesignTabsBuilder>,
  widgetDesignTabsArray: WidgetDesignTabsArray,
  t: TFunction
) => {
  widgetDesignTabsArray.forEach(({ label, roles, dependents, tooltip }) => {
    addTab(widgetDesignTabsBuilder, t(label), roles, dependents, tooltip ? t(tooltip) : undefined);
  });
};

export const arrayToStringWithSeparator = (
  array: (number | DispatchType | string)[],
  separator: string
) => {
  return array.join(separator);
};

export const getSortedArrayByIds = <T extends { id: string }>(
  array: T[],
  sortedIds: string[] | undefined
): T[] => {
  if (!sortedIds || sortedIds.length === 0) {
    return array;
  }

  return [...array].sort((item1, item2) => {
    const item1Index = sortedIds.indexOf(item1.id);
    const item2Index = sortedIds.indexOf(item2.id);
    return item1Index === -1 || item1Index > item2Index ? 1 : -1;
  });
};

export const disableWidgetComponentSelection = (
  builder: WidgetBuilder,
  componentId: string,
  selectable = false
) => {
  builder.configureConnectedComponents(getRole(componentId), (titleAndDescriptionBuilder) => {
    titleAndDescriptionBuilder.behavior().set({ closed: { hideFromHierarchy: true, selectable } });
  });
};

const getControllerWidgetById = (appManifest: AppManifest, id: string) => {
  return appManifest.controllersStageData![id].default;
};

export const setLightboxGfpp = (
  appManifestBuilder: AppManifestBuilder,
  id: string,
  displayName?: string
) => {
  appManifestBuilder.configureController(
    `${LIGHTBOX_CONNECTED_PARAMS.CONTROLLER}_${id}`,
    (controllerBuilder) => {
      controllerBuilder.configureConnectedComponents(
        `${LIGHTBOX_CONNECTED_PARAMS.ROLE}_${id}`,
        (componentBuilder) => {
          displayName && componentBuilder.set({ displayName });
          componentBuilder.behavior();

          componentBuilder
            .gfpp()
            .set('mainAction2', { behavior: 'HIDE' })
            .set('layout', { behavior: 'HIDE' })
            .set('settings', { behavior: 'HIDE' });
        }
      );
    }
  );
};

export const setOloGfpp = async (
  appManifest: AppManifest,
  httpClient: IHttpClient,
  errorHandler: ErrorHandlerPublicAPI
) => {
  await setCartButtonSettingsGfpp(appManifest, httpClient, errorHandler);
  setOloMobileGfpp(appManifest);
};

const setCartButtonSettingsGfpp = async (
  appManifest: AppManifest,
  httpClient: IHttpClient,
  errorHandler: ErrorHandlerPublicAPI
) => {
  const popups = await getAllPopups(httpClient, errorHandler);
  const isSideCartInstalled = popups?.find((popup) => popup.title === 'Side Cart');
  if (!isSideCartInstalled) {
    const cartButtonWidget = getControllerWidgetById(appManifest, CART_BUTTON_CONTROLLER_WIDGET_ID);
    // @ts-expect-error
    delete cartButtonWidget?.connections?.InnerCartBox?.gfpp?.desktop?.mainAction1;
  }
};

export const disableWidgetDuplaction = (appManifest: AppManifest) => {
  const [
    oloStageDataDefault,
    headerStageDataDefault,
    menuStageDataDefault,
    sectionStageDataDefault,
    dishesStageDataDefault,
    navigationStageDataDefault,
    cartButtonStageDataDefault,
  ] = getControllerWidgets(appManifest, [
    OLO_CONTROLLER_WIDGET_ID,
    HEADER_CONTROLLER_WIDGET_ID,
    MENU_CONTROLLER_WIDGET_ID,
    SECTION_CONTROLLER_WIDGET_ID,
    DISHES_CONTROLLER_WIDGET_ID,
    NAVIGATION_CONTROLLER_WIDGET_ID,
    CART_BUTTON_CONTROLLER_WIDGET_ID,
  ]);

  const stageDataDefaultArr = [
    oloStageDataDefault,
    headerStageDataDefault,
    ...getChildComponents(headerStageDataDefault, [
      HEADER_WIDGET_COMPONENT_IDS.titleAndDescContainer,
      HEADER_WIDGET_COMPONENT_IDS.badgesContainer,
      HEADER_WIDGET_COMPONENT_IDS.fulfillmentPicker,
      HEADER_WIDGET_COMPONENT_IDS.fulfillmentContainer,
    ]),
    menuStageDataDefault,
    sectionStageDataDefault,
    navigationStageDataDefault,
    dishesStageDataDefault,
    cartButtonStageDataDefault,
  ].filter((widget): widget is WidgetStateStageData => !!widget);

  stageDataDefaultArr.forEach((widget) => {
    widget.behavior = { ...widget.behavior, duplicatable: false };
  });
};

const setOloMobileGfpp = (appManifest: AppManifest) => {
  const [
    oloStageDataDefault,
    headerStageDataDefault,
    menuStageDataDefault,
    sectionStageDataDefault,
    dishesStageDataDefault,
    navigationStageDataDefault,
    cartButtonStageDataDefault,
    ghostStateStageDataDefault,
  ] = getControllerWidgets(appManifest, [
    OLO_CONTROLLER_WIDGET_ID,
    HEADER_CONTROLLER_WIDGET_ID,
    MENU_CONTROLLER_WIDGET_ID,
    SECTION_CONTROLLER_WIDGET_ID,
    DISHES_CONTROLLER_WIDGET_ID,
    NAVIGATION_CONTROLLER_WIDGET_ID,
    CART_BUTTON_CONTROLLER_WIDGET_ID,
    GHOST_STATE_CONTROLLER_WIDGET_ID,
  ]);

  const stageDataDefaultArr = [
    oloStageDataDefault,
    headerStageDataDefault,
    ...getChildComponents(headerStageDataDefault, [
      HEADER_WIDGET_COMPONENT_IDS.titleAndDescContainer,
      HEADER_WIDGET_COMPONENT_IDS.badgesContainer,
      HEADER_WIDGET_COMPONENT_IDS.fulfillmentPicker,
      HEADER_WIDGET_COMPONENT_IDS.fulfillmentContainer,
    ]),
    navigationStageDataDefault,
    dishesStageDataDefault,
    cartButtonStageDataDefault,
    ghostStateStageDataDefault,
  ].filter((widget): widget is WidgetStateStageData => !!widget);
  addMobileDesign(stageDataDefaultArr);

  const { gfpp: oloGfpp } = oloStageDataDefault;
  const { gfpp: dishesGfpp } = dishesStageDataDefault;
  const [{ gfpp: cartButtonGfpp }] = getChildComponents(cartButtonStageDataDefault, [
    CART_BUTTON_COMPONENT_IDS.container,
  ]);

  addMobileGfpp(oloGfpp as GfppConfig, ['mainAction1', 'mainAction2']);
  addMobileGfpp(cartButtonGfpp as GfppConfig, ['mainAction1']);
  const menuComponents = getChildComponents(menuStageDataDefault, [
    MENU_WIDGET_COMPONENT_IDS.menuTitle,
    MENU_WIDGET_COMPONENT_IDS.menuDescription,
  ]);
  menuComponents.forEach((component) => {
    addMobileGfpp(component.gfpp as GfppConfig, ['mainAction2']);
  });
  const sectionComponents = getChildComponents(sectionStageDataDefault, [
    SECTION_WIDGET_COMPONENT_IDS.sectionTitle,
    SECTION_WIDGET_COMPONENT_IDS.sectionDescription,
    SECTION_WIDGET_COMPONENT_IDS.sectionImage,
  ]);
  sectionComponents.forEach((component) => {
    addMobileGfpp(component.gfpp as GfppConfig, ['mainAction1', 'mainAction2']);
  });
  addMobileGfpp(dishesGfpp as GfppConfig, ['mainAction1', 'iconButtons']);
};

const addMobileDesign = (widgetStateData: (WidgetStateStageData | ConnectedComponentConfig)[]) => {
  widgetStateData.forEach((widget: WidgetStateStageData) => {
    widget.gfpp && addMobileGfpp(widget.gfpp, ['widgetDesign']);
    if (widget.connections) {
      const componentConfigs = Object.values(widget.connections);
      componentConfigs.forEach((config) => {
        config?.gfpp && addMobileGfpp(config.gfpp, ['widgetDesign']);
      });
    }
  });
};

const addMobileGfpp = (gfppConfig: GfppConfig, gfpps: (keyof GfppDesktopConfig)[]) => {
  if (gfppConfig.desktop && gfppConfig.mobile) {
    gfpps.forEach((gfpp) => {
      // @ts-expect-error
      gfppConfig.mobile[gfpp] = { ...(gfppConfig.desktop as GfppDesktopConfig)[gfpp] };
    });
  }
};

const getChildComponents = (
  widgetState: WidgetStateStageData | ControllerStateStageData,
  elementIds: string[]
) => {
  return elementIds.map((elementId) => widgetState.connections?.[getRole(elementId)] ?? {});
};

const getControllerWidgets = (appManifest: AppManifest, widgetControllerIds: string[]) => {
  return widgetControllerIds.map((widgetId) => getControllerWidgetById(appManifest, widgetId));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type F<A extends any[], R> = (...args: A) => Promise<R>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const monitorCallback = <A extends any[], R>(
  callback: F<A, R>,
  breadcrumb?: {
    type?: string;
    category?: string;
    message: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: { [key: string]: any };
  },
  reportError?: ReportError,
  errorMonitor?: PanoramaErrorMonitor<ErrorMonitorUserData>
): F<A, R> => {
  return (...args: A): Promise<R> => {
    breadcrumb && errorMonitor?.addBreadcrumb({ ...breadcrumb, data: { args } });
    try {
      return callback(...args);
    } catch (error) {
      reportError?.(error as Error);
      errorMonitor?.reportError(error as Error);
      return Promise.reject(error);
    }
  };
};

export const openDispatchModal = async ({
  onSave,
  context,
  dispatchState,
}: {
  onSave: ({
    dispatchType,
    dispatchInfo,
    operationId,
    isLocationChanged,
  }: {
    dispatchType: DispatchType;
    dispatchInfo: DispatchInfo;
    operationId: string;
    isLocationChanged: boolean;
  }) => Promise<void>;
  context: RootContext;
  dispatchState?: DispatchStateByLocation;
}) => {
  await context.ModalService?.openDispatchModal({
    dispatchState,
    biReporterService: context.biReporterService,
    fedopsLogger: context.fedopsLogger,
    currentOperationId: _dispatchState.currentOperationId,
    operations: _dispatchState.operations,
    isLocationSelected: _dispatchState.isLocationSelected,
    selectedDispatchType: _dispatchState.dispatchType,
    onSave,
  });
};

export const openItemOrDispatchModal = async ({
  enableMultiLocation,
  shouldOpenItemModalBeforeDispatch,
  isMemberLoggedIn,
  itemData,
  menuId,
  sectionId,
  cartItem,
  onSave,
  context,
  locale,
  timezone,
  t,
}: {
  enableMultiLocation: boolean;
  shouldOpenItemModalBeforeDispatch: boolean;
  isMemberLoggedIn?: boolean;
  itemData: ItemData;
  menuId: string;
  sectionId: string;
  cartItem?: CartLineItem;
  onSave: ({
    dispatchType,
    dispatchInfo,
    operationId,
    isLocationChanged,
  }: {
    dispatchType: DispatchType;
    dispatchInfo: DispatchInfo;
    operationId: string;
    isLocationChanged: boolean;
  }) => Promise<void>;
  context: RootContext;
  locale: string;
  timezone: string;
  t: TFunction;
}) => {
  const isLocationSelected = !(
    _dispatchState.isMultiLocation && !_dispatchState.isLocationSelected
  );
  const ghostMode = enableMultiLocation && _dispatchState.isMultiLocation && !isLocationSelected;
  const hasAddress = !!_dispatchState.dispatchInfo.address;
  const availableDispatchTypes = !!_dispatchState.availableDispatchTypesForCurrentLocation?.length;
  const shouldOpenDishModal =
    (hasAddress || !availableDispatchTypes || shouldOpenItemModalBeforeDispatch) && !ghostMode;

  if (shouldOpenDishModal) {
    const shouldAddItemToCart = hasAddress;
    return openItemModal(
      context,
      itemData,
      menuId,
      sectionId,
      shouldAddItemToCart,
      locale,
      timezone,
      t,
      onSave,
      cartItem
    );
  } else {
    context.biReporterService?.reportOloLiveSiteClickOnFulfillmentBiEvent({
      origin: LiveSiteClickFulfillmentOrigin.CLICK_ITEM_NO_ADDRESS,
      dispatchType: _dispatchState.selectedDispatchType,
      isMemberLoggedIn,
    });
    openDispatchModal({ onSave, context, dispatchState: _dispatchState.state });
  }
};

export const openItemModal = async (
  context: RootContext,
  itemData: ItemData,
  menuId: string,
  sectionId: string,
  shouldAddItemToCart: boolean,
  locale: string,
  timezone: string,
  t: TFunction,
  onSave: ({
    dispatchType,
    dispatchInfo,
    operationId,
    isLocationChanged,
  }: {
    dispatchType: DispatchType;
    dispatchInfo: DispatchInfo;
    operationId: string;
    isLocationChanged: boolean;
  }) => Promise<void>,
  cartItem?: CartLineItem
) => {
  const menu = menusState.getMenu(menuId);
  const { isMenuOfItemAvailable, hasNextAvailability, text, shouldCollapseAvailabilityStatus } =
    getAvailabilityStatusProps({
      menu,
      locale,
      timezone,
      t,
      keys: availabilityStatusKeys.itemModal,
    });

  const dishModalRes = await context.ModalService?.openDishModal({
    item: itemData,
    cartService: context.CartService,
    biReporterService: context.biReporterService,
    operation: _dispatchState.currentOperation,
    canAcceptOrders: _dispatchState.isSelectedDispatchAvailable(),
    menuId,
    sectionId,
    cartItem,
    fedopsLogger: context.fedopsLogger,
    availabilityStatusProps: {
      isMenuOfItemAvailable,
      text,
      shouldCollapseAvailabilityStatus,
      hasNextAvailability,
      dispatchType: _dispatchState.selectedDispatchType,
    },
    shouldAddItemToCart,
    openDispatchModal: async () =>
      openDispatchModal({ onSave, context, dispatchState: _dispatchState.state }),
  });
  return dishModalRes;
};

export const onItemModalClosed = async (
  itemData: ItemData,
  menuId: string,
  sectionId: string,
  context: RootContext,
  t: TFunction,
  dishModalResponse: {
    data: Promise<{ error: ADD_TO_CART_ERRORS; cartItem: CartLineItem }>;
    additionaldata: { cartLineItem: CartLineItem; isUpdate: boolean };
  },
  site: ISiteApis
) => {
  const { data: dataPromise, additionaldata } = dishModalResponse ?? {};
  const cartLineItemsKey = `${menuId}_${sectionId}_${itemData._id}`;
  const cartLineItems = cartState.cartLineItems.get(cartLineItemsKey) ?? [];
  additionaldata.cartLineItem &&
    !additionaldata.isUpdate &&
    cartState.cartLineItems.set(cartLineItemsKey, [...cartLineItems, additionaldata.cartLineItem]);

  const data = await dataPromise;

  if (data?.error !== undefined) {
    const currentCart = await context.CartService?.getCurrentCart?.();

    const getLocationNames = () => {
      const cartLocationId = currentCart?.businessLocationId;
      const curLocationName = _dispatchState.currentOperation?.locationDetails?.name;
      const cartLocationName = _dispatchState.operations.find(
        (op) => op.locationId === cartLocationId
      )?.locationDetails?.name;
      return { curLocation: curLocationName, prevLocation: cartLocationName };
    };

    const getPageNames = async () => {
      const cartOperationId = (
        currentCart?.lineItems?.[0]?.catalogReference?.options as CatalogOptions
      )?.operationId;
      const curOrderingPage = await getCurrentPageName(site);
      const cartOperationGroupId = _dispatchState.operations.find(
        (operation) => operation.id === cartOperationId
      )?.operationGroupId;
      const cartPageName = await getPageNameByOperationId(site, cartOperationGroupId ?? '');
      return { curOrderingPage, prevOrderingPage: cartPageName };
    };

    const getVerticalNames = async () => {
      const curOrderingPage = await getCurrentPageName(site);
      const cartVerticalId = currentCart?.lineItems?.[0].catalogReference?.appId;
      const cartVerticalName = await getPageNameByAppDefIdId(site, cartVerticalId ?? '');
      return { curVertical: curOrderingPage, prevVertical: cartVerticalName };
    };

    const titles = {
      [ADD_TO_CART_ERRORS.MIXED_CART]: t('cart.mixed-vertical-cart-error.title'),
      [ADD_TO_CART_ERRORS.MIXED_CART_OPERATION]: t('cart.mixed-cart-by-operation-error.title'),
      [ADD_TO_CART_ERRORS.MIXED_CART_LOCATION]: t('cart.mixed-cart-by-location-error.title'),
    };

    const contents = {
      [ADD_TO_CART_ERRORS.MIXED_CART]: t(
        'cart.mixed-vertical-cart-error.content',
        await getVerticalNames()
      ),
      [ADD_TO_CART_ERRORS.MIXED_CART_OPERATION]: t(
        'cart.mixed-cart-by-operation-error.content',
        await getPageNames()
      ),
      [ADD_TO_CART_ERRORS.MIXED_CART_LOCATION]: t(
        'cart.mixed-cart-by-location-error.content',
        getLocationNames()
      ),
    };
    const content = contents[data?.error];
    const title = titles[data?.error];
    if (content && title) {
      context.ModalService?.openErrorModal({
        title,
        content,
        closeButtonLabel: t('cart.mixed-vertical-cart-error.button'),
      });
      cartState.cartLineItems.set(cartLineItemsKey, cartLineItems);
      return;
    }
  }

  if (data && !data.cartItem) {
    cartState.cartLineItems.set(cartLineItemsKey, cartLineItems);
    context.ModalService?.openErrorModal({
      title: t('addToCart.error-modal.general-error.title'),
      content: t('addToCart.error-modal.general-error.content'),
      closeButtonLabel: t('addToCart.error-modal.general-error.button'),
    });
  }
};
